import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Reusable WebSocket hook for the Gulf Coast Work messaging fabric.
 * - Connects to `wss://<backend>/api/ws/messages?token=<bearer>` (mounted in server.py).
 * - Reconnects with exponential backoff (2s, 4s, 8s, capped at 15s).
 * - Emits parsed JSON messages via `onMessage` and lets the caller broadcast
 *   client-to-server events with `send()`.
 */
export function useWebSocket({ onMessage, enabled = true }) {
  const wsRef = useRef(null);
  const backoffRef = useRef(2000);
  const timerRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  const connect = useCallback(() => {
    if (!enabled) return;
    const token = typeof window !== "undefined" ? window.localStorage.getItem("gcw_bearer") : null;
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const proto = base.startsWith("https") ? "wss" : "ws";
    const host = base.replace(/^https?:\/\//, "");
    const url = `${proto}://${host}/api/ws/messages?token=${encodeURIComponent(token)}`;
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      setStatus("connecting");
      ws.onopen = () => {
        setStatus("open");
        backoffRef.current = 2000;
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (handlerRef.current) handlerRef.current(msg);
        } catch (_) { /* ignore */ }
      };
      ws.onclose = () => {
        setStatus("closed");
        if (!enabled) return;
        // Reconnect with backoff
        timerRef.current = setTimeout(() => {
          backoffRef.current = Math.min(backoffRef.current * 2, 15000);
          connect();
        }, backoffRef.current);
      };
      ws.onerror = () => {
        setStatus("error");
        try { ws.close(); } catch (_) {}
      };
    } catch (_) {
      setStatus("error");
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    connect();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      try { wsRef.current?.close(); } catch (_) {}
      wsRef.current = null;
    };
  }, [connect, enabled]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    try {
      ws.send(typeof payload === "string" ? payload : JSON.stringify(payload));
      return true;
    } catch (_) { return false; }
  }, []);

  return { status, send };
}
